import React, { useState } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import LikeIcon from '@atlaskit/icon/glyph/like';
import { Inline, Flex, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { AutoreviewFeedbackSentiment } from 'src/components/conversation/src/internal/analytics';

import messages from './autoreview-feedback.i18n';

interface TooltipContentProps {
  openTooltip: boolean;
  sentiment: AutoreviewFeedbackSentiment;
}
const TooltipContent = ({ openTooltip, sentiment }: TooltipContentProps) => {
  const { formatMessage } = useIntl();

  return openTooltip ? (
    <Flex alignItems="center" gap="space.050">
      <CheckCircleIcon
        primaryColor={token('color.icon.inverse', '#FFFFFF')}
        secondaryColor={token(
          'color.background.neutral.bold.pressed',
          '#172B4D'
        )}
        label="Submitted"
        size="small"
      />
      <Text size="small" color="color.text.inverse">
        Feedback submitted
      </Text>
    </Flex>
  ) : (
    <Text size="small" color="color.text.inverse">
      {sentiment === 'positive'
        ? formatMessage(messages.goodResponseLabel)
        : formatMessage(messages.badResponseLabel)}
    </Text>
  );
};

interface ThumbsUpThumbsDownProps {
  sentiment: AutoreviewFeedbackSentiment;
  handleFeedbackClick: (x: string) => void;
}

/* eslint @typescript-eslint/ban-types: "warn" */
export const ThumbsUpThumbsDown: React.FC<ThumbsUpThumbsDownProps> = ({
  sentiment,
  handleFeedbackClick,
}) => {
  const [openTooltip, setOpenTooltip] = useState<boolean>(false);
  const { formatMessage } = useIntl();

  const handleClick = (setSentiment: string) => {
    setOpenTooltip(true);
    setTimeout(() => setOpenTooltip(false), 2000);
    handleFeedbackClick(setSentiment);
  };

  return (
    <Inline space="space.050" alignBlock="center">
      <Inline space="space.050">
        <Text weight="bold">
          {formatMessage(messages.howDidAutoreviewDoMessage)}
        </Text>
        <Text>{formatMessage(messages.rateAutoreviewCommentMessage)}</Text>
      </Inline>
      <Tooltip
        content={
          <TooltipContent openTooltip={openTooltip} sentiment="positive" />
        }
      >
        {tooltipProps => (
          <Flex
            alignItems="center"
            justifyContent="center"
            xcss={xcss({
              borderRadius: 'border.radius.100',
              backgroundColor:
                sentiment === 'positive'
                  ? 'color.background.neutral.subtle.pressed'
                  : 'color.background.neutral.subtle',
            })}
          >
            <IconButton
              {...tooltipProps}
              icon={iconProps => (
                <LikeIcon
                  {...iconProps}
                  size="small"
                  primaryColor={token('color.icon.subtle', '#626F86')}
                />
              )}
              label={formatMessage(messages.goodResponseLabel)}
              spacing="compact"
              appearance="subtle"
              onClick={() => handleClick('positive')}
              testId="thumbs-up"
            />
          </Flex>
        )}
      </Tooltip>
      <Tooltip
        content={
          <TooltipContent openTooltip={openTooltip} sentiment="negative" />
        }
      >
        {tooltipProps => (
          <Flex
            alignItems="center"
            justifyContent="center"
            xcss={xcss({
              borderRadius: 'border.radius.100',
              backgroundColor:
                sentiment === 'negative'
                  ? 'color.background.neutral.subtle.pressed'
                  : 'color.background.neutral.subtle',
            })}
          >
            <IconButton
              {...tooltipProps}
              icon={iconProps => (
                // There is no Dislike icon so this Inline rotates the Like icon 180deg
                <Inline
                  {...iconProps}
                  xcss={xcss({ transform: 'rotate(180deg)' })}
                >
                  <LikeIcon
                    size="small"
                    primaryColor={token('color.icon.subtle', '#626F86')}
                    label=""
                  />
                </Inline>
              )}
              spacing="compact"
              label={formatMessage(messages.badResponseLabel)}
              appearance="subtle"
              onClick={() => handleClick('negative')}
              testId="thumbs-down"
            />
          </Flex>
        )}
      </Tooltip>
    </Inline>
  );
};
